import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { hi as hiLocale } from "date-fns/locale";
import { Phone, EnvelopeSimple, MapPin, PaperPlaneTilt, QrCode, CheckCircle, CalendarCheck, Clock, Buildings } from "@phosphor-icons/react";
import { useSeo } from "../lib/useSeo";
import { COMPANY } from "../lib/constants";
import { useLang } from "../lib/i18n";
import { submitContact, bookDemo, getBookedSlots } from "../lib/api";
import { SectionBadge, Reveal } from "../components/blocks";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const initial = { name: "", email: "", phone: "", company: "", product_interest: "General", message: "" };

const STR = {
  en: {
    badge: "Contact Us",
    t1: "Let's build the ", grad: "future", t2: " together.",
    sub: "Book a demo, talk to our team, or ask us anything about our products.",
    name: "Name *", namePh: "Your full name", email: "Email *", phone: "Phone", company: "Company", companyPh: "Company / Organisation",
    interest: "Product Interest", message: "Message *", messagePh: "Tell us how we can help...",
    send: "Send Message", sending: "Sending...",
    doneT: "Message received!", doneSub: "Thank you for reaching out to Priyajit Systems. Our team will get back to you soon.", another: "Send another message",
    errFill: "Please fill in your name, email and message.", ok: "Thanks! We'll be in touch shortly.", errSend: "Something went wrong. Please try again.",
    lblPhone: "Phone", lblEmail: "Email", lblAddress: "Address", visit: "Visit our website",
    demoBadge: "Book a Demo Slot",
    demoTitle: "Pick a date & time — we'll call you.",
    demoSub: "Choose a convenient slot and our team will walk you through Atikxzio HMS in a live 30-minute demo. No preparation needed.",
    demoStepDate: "1 · Pick a date", demoStepSlot: "2 · Pick a time slot", demoStepDetails: "3 · Your details",
    demoName: "Name *", demoNamePh: "Your full name", demoPhone: "Phone *", demoHospital: "Hospital / Organisation", demoHospitalPh: "Hospital name",
    demoBook: "Confirm Demo Slot", demoBooking: "Booking...",
    demoPickDateFirst: "Pick a date to see available slots",
    demoErr: "Please pick a date, a time slot, and fill your name & phone.",
    demoOk: "Demo slot booked!", demoTaken: "That slot was just booked. Please pick another.",
    demoDoneT: "Your demo is booked!", demoDoneSub: "Our team will call you at the chosen time for a live Atikxzio HMS demo.",
    demoAnother: "Book another slot", slotBooked: "Booked",
    bizTitle: "Business details",
    bizLegal: "Legal name", bizCin: "CIN", bizGstin: "GSTIN", bizOffice: "Registered office",
    bizHours: "Support hours", bizGrievance: "Grievance Officer", bizGrievanceNote: "Replies within 48 hours",
  },
  hi: {
    badge: "संपर्क करें",
    t1: "आइए मिलकर बनाएँ ", grad: "भविष्य", t2: "।",
    sub: "डेमो बुक करें, हमारी टीम से बात करें, या हमारे प्रोडक्ट्स के बारे में कुछ भी पूछें।",
    name: "नाम *", namePh: "आपका पूरा नाम", email: "ईमेल *", phone: "फ़ोन", company: "संस्था", companyPh: "कंपनी / अस्पताल का नाम",
    interest: "किस प्रोडक्ट में रुचि है", message: "संदेश *", messagePh: "बताइए हम कैसे मदद कर सकते हैं...",
    send: "संदेश भेजें", sending: "भेजा जा रहा है...",
    doneT: "संदेश मिल गया!", doneSub: "प्रियाजित सिस्टम्स से संपर्क करने के लिए धन्यवाद। हमारी टीम जल्द आपसे बात करेगी।", another: "एक और संदेश भेजें",
    errFill: "कृपया नाम, ईमेल और संदेश भरें।", ok: "धन्यवाद! हम जल्द संपर्क करेंगे।", errSend: "कुछ गड़बड़ हुई। कृपया दोबारा कोशिश करें।",
    lblPhone: "फ़ोन", lblEmail: "ईमेल", lblAddress: "पता", visit: "हमारी वेबसाइट देखें",
    demoBadge: "डेमो स्लॉट बुक करें",
    demoTitle: "तारीख़ और समय चुनिए — हम कॉल करेंगे।",
    demoSub: "सुविधाजनक स्लॉट चुनिए — हमारी टीम 30 मिनट के लाइव डेमो में आपको Atikxzio HMS दिखाएगी। किसी तैयारी की ज़रूरत नहीं।",
    demoStepDate: "1 · तारीख़ चुनें", demoStepSlot: "2 · समय चुनें", demoStepDetails: "3 · आपकी जानकारी",
    demoName: "नाम *", demoNamePh: "आपका पूरा नाम", demoPhone: "फ़ोन *", demoHospital: "अस्पताल / संस्था", demoHospitalPh: "अस्पताल का नाम",
    demoBook: "डेमो स्लॉट पक्का करें", demoBooking: "बुक हो रहा है...",
    demoPickDateFirst: "उपलब्ध स्लॉट देखने के लिए तारीख़ चुनें",
    demoErr: "कृपया तारीख़, समय स्लॉट और नाम-फ़ोन भरें।",
    demoOk: "डेमो स्लॉट बुक हो गया!", demoTaken: "यह स्लॉट अभी-अभी बुक हुआ। कृपया दूसरा चुनें।",
    demoDoneT: "आपका डेमो बुक हो गया!", demoDoneSub: "चुने गए समय पर हमारी टीम Atikxzio HMS के लाइव डेमो के लिए आपको कॉल करेगी।",
    demoAnother: "एक और स्लॉट बुक करें", slotBooked: "बुक हो चुका",
    bizTitle: "व्यावसायिक जानकारी",
    bizLegal: "क़ानूनी नाम", bizCin: "CIN", bizGstin: "GSTIN", bizOffice: "पंजीकृत कार्यालय",
    bizHours: "सपोर्ट समय", bizGrievance: "शिकायत अधिकारी", bizGrievanceNote: "48 घंटे के भीतर उत्तर",
  },
};

const SLOTS = ["10:00 AM", "11:30 AM", "1:00 PM", "3:00 PM", "4:30 PM", "6:00 PM"];

const DemoSlotBooking = ({ s, lang }) => {
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [booked, setBooked] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", hospital: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const dateStr = date ? format(date, "yyyy-MM-dd") : "";
  const locale = lang === "hi" ? hiLocale : undefined;

  useEffect(() => {
    if (!dateStr) return;
    setSlot("");
    getBookedSlots(dateStr).then((r) => setBooked(r.slots)).catch(() => setBooked([]));
  }, [dateStr]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!date || !slot || !form.name || !form.phone) {
      toast.error(s.demoErr);
      return;
    }
    setLoading(true);
    try {
      const b = await bookDemo({ ...form, demo_date: dateStr, demo_slot: slot, product: "Atikxzio HMS" });
      setDone(b);
      toast.success(s.demoOk);
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error(s.demoTaken);
        getBookedSlots(dateStr).then((r) => setBooked(r.slots)).catch(() => {});
        setSlot("");
      } else {
        toast.error(s.errSend);
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setDone(null); setDate(null); setSlot(""); setForm({ name: "", phone: "", hospital: "" }); };

  return (
    <div className="rounded-[2rem] border border-teal/20 bg-white p-7 shadow-[0_12px_40px_rgba(16,185,129,0.1)] sm:p-10" data-testid="demo-booking-card">
      {done ? (
        <div className="flex flex-col items-center py-14 text-center" data-testid="demo-success">
          <CalendarCheck size={56} weight="fill" className="text-teal" />
          <h3 className="mt-5 font-display text-2xl font-black text-navy">{s.demoDoneT}</h3>
          <p className="mt-2 max-w-sm text-slate-600">{s.demoDoneSub}</p>
          <p className="mt-5 rounded-full bg-teal/10 px-5 py-2 font-display text-sm font-black text-teal" data-testid="demo-success-details">
            {format(new Date(done.demo_date + "T00:00:00"), "d MMMM yyyy", { locale })} · {done.demo_slot}
          </p>
          <button onClick={reset} className="mt-6 rounded-full border border-navy/20 px-6 py-2.5 text-sm font-bold text-navy transition-all hover:bg-navy hover:text-white" data-testid="demo-book-another">{s.demoAnother}</button>
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-teal">{s.demoStepDate}</p>
            <div className="inline-block rounded-2xl border border-navy/[0.08] p-2" data-testid="demo-date-calendar">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={{ before: new Date() }}
                locale={locale}
              />
            </div>
          </div>
          <div className="flex flex-col">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-teal">{s.demoStepSlot}</p>
            {date ? (
              <div className="grid grid-cols-3 gap-2.5" data-testid="demo-slot-grid">
                {SLOTS.map((sl, i) => {
                  const taken = booked.includes(sl);
                  return (
                    <button
                      key={sl}
                      type="button"
                      disabled={taken}
                      onClick={() => setSlot(sl)}
                      className={`flex flex-col items-center rounded-xl border px-2 py-2.5 text-sm font-bold transition-all ${
                        taken
                          ? "cursor-not-allowed border-navy/[0.06] bg-slate-50 text-slate-300 line-through"
                          : slot === sl
                            ? "border-teal bg-teal text-white shadow-[0_6px_16px_rgba(16,185,129,0.35)]"
                            : "border-navy/15 text-navy hover:border-teal hover:text-teal"
                      }`}
                      data-testid={`demo-slot-${i}`}
                    >
                      <span className="flex items-center gap-1.5"><Clock size={14} weight="bold" /> {sl}</span>
                      {taken && <span className="mt-0.5 text-[10px] font-semibold no-underline">{s.slotBooked}</span>}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="rounded-xl bg-slate-50 px-4 py-6 text-center text-sm font-semibold text-slate-400" data-testid="demo-pick-date-hint">{s.demoPickDateFirst}</p>
            )}

            <p className="mb-3 mt-7 text-xs font-bold uppercase tracking-[0.18em] text-teal">{s.demoStepDetails}</p>
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Input value={form.name} onChange={set("name")} placeholder={s.demoNamePh} aria-label={s.demoName} className="h-12 rounded-xl border-navy/15" data-testid="demo-input-name" />
                <Input value={form.phone} onChange={set("phone")} placeholder="+91 ..." aria-label={s.demoPhone} className="h-12 rounded-xl border-navy/15" data-testid="demo-input-phone" />
              </div>
              <Input value={form.hospital} onChange={set("hospital")} placeholder={s.demoHospitalPh} aria-label={s.demoHospital} className="h-12 rounded-xl border-navy/15" data-testid="demo-input-hospital" />
            </div>
            <button
              type="button"
              onClick={submit}
              disabled={loading}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal px-7 py-4 text-sm font-bold text-white shadow-[0_8px_24px_rgba(16,185,129,0.4)] transition-all hover:-translate-y-0.5 disabled:opacity-60"
              data-testid="demo-book-submit"
            >
              {loading ? s.demoBooking : <><CalendarCheck size={18} weight="fill" /> {s.demoBook}</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Contact() {
  const { lang } = useLang();
  const s = STR[lang];

  useSeo({
    title: "Contact | Priyajit Systems Private Limited",
    description: "Get in touch with Priyajit Systems. Book a demo, talk to our team, or reach out about Atikxzio CMH and Atikxzio HMS.",
    image: "/og-image.jpg",
  });

  const [params] = useSearchParams();
  const [form, setForm] = useState(() =>
    params.get("interest") === "founding"
      ? { ...initial, product_interest: "Founding Partner" }
      : initial
  );
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(s.errFill);
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setDone(true);
      setForm(initial);
      toast.success(s.ok);
    } catch (err) {
      toast.error(s.errSend);
    } finally {
      setLoading(false);
    }
  };

  const details = [
    { icon: Phone, label: s.lblPhone, value: COMPANY.phone, href: `tel:${COMPANY.phoneTel}` },
    { icon: EnvelopeSimple, label: s.lblEmail, value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: MapPin, label: s.lblAddress, value: COMPANY.address, href: null },
  ];

  return (
    <div>
      <section className="relative overflow-hidden bg-navy-deep grain pt-36 pb-24 lg:pt-44" data-testid="contact-hero">
        <div aria-hidden className="absolute inset-0 bg-grid opacity-50" />
        <div aria-hidden className="absolute -left-24 top-10 h-96 w-96 rounded-full bg-electric/20 blur-[110px]" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <div className="text-slate-200"><SectionBadge number="00" label={s.badge} tone="light" /></div>
          <h1 className="mt-6 max-w-3xl font-display text-5xl font-black tracking-tighter text-white sm:text-6xl lg:text-7xl">
            {s.t1}<span className="text-gradient-electric">{s.grad}</span>{s.t2}
          </h1>
          <p className="mt-6 max-w-xl text-lg text-slate-300">{s.sub}</p>
        </div>
      </section>

      {/* Demo slot booking */}
      <section id="book-demo" className="bg-[#F8FAFC] pt-20 lg:pt-28" data-testid="demo-booking-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full bg-teal/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-teal" data-testid="demo-booking-badge">
              <CalendarCheck size={14} weight="fill" /> {s.demoBadge}
            </span>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">{s.demoTitle}</h2>
            <p className="mt-3 max-w-2xl text-slate-600">{s.demoSub}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8"><DemoSlotBooking s={s} lang={lang} /></div>
          </Reveal>
        </div>
      </section>


      <section className="bg-[#F8FAFC] py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-12 lg:px-8">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="rounded-[2rem] border border-navy/[0.06] bg-white p-7 shadow-[0_12px_40px_rgba(11,30,77,0.08)] sm:p-10" data-testid="contact-form-card">
              {done ? (
                <div className="flex flex-col items-center py-16 text-center" data-testid="contact-success">
                  <CheckCircle size={56} weight="fill" className="text-teal" />
                  <h3 className="mt-5 font-display text-2xl font-black text-navy">{s.doneT}</h3>
                  <p className="mt-2 max-w-sm text-slate-600">{s.doneSub}</p>
                  <button onClick={() => setDone(false)} className="mt-6 rounded-full border border-navy/20 px-6 py-2.5 text-sm font-bold text-navy transition-all hover:bg-navy hover:text-white" data-testid="contact-send-another">{s.another}</button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <Label className="mb-1.5 block text-sm font-bold text-navy">{s.name}</Label>
                      <Input value={form.name} onChange={set("name")} placeholder={s.namePh} className="h-12 rounded-xl border-navy/15" data-testid="contact-input-name" />
                    </div>
                    <div>
                      <Label className="mb-1.5 block text-sm font-bold text-navy">{s.email}</Label>
                      <Input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="h-12 rounded-xl border-navy/15" data-testid="contact-input-email" />
                    </div>
                    <div>
                      <Label className="mb-1.5 block text-sm font-bold text-navy">{s.phone}</Label>
                      <Input value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="h-12 rounded-xl border-navy/15" data-testid="contact-input-phone" />
                    </div>
                    <div>
                      <Label className="mb-1.5 block text-sm font-bold text-navy">{s.company}</Label>
                      <Input value={form.company} onChange={set("company")} placeholder={s.companyPh} className="h-12 rounded-xl border-navy/15" data-testid="contact-input-company" />
                    </div>
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-sm font-bold text-navy">{s.interest}</Label>
                    <Select value={form.product_interest} onValueChange={(v) => setForm((f) => ({ ...f, product_interest: v }))}>
                      <SelectTrigger className="h-12 rounded-xl border-navy/15" data-testid="contact-select-product"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Atikxzio CMH">Atikxzio CMH</SelectItem>
                        <SelectItem value="Atikxzio HMS">Atikxzio HMS</SelectItem>
                        <SelectItem value="Founding Partner">Founding Partner</SelectItem>
                        <SelectItem value="General">General</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-sm font-bold text-navy">{s.message}</Label>
                    <Textarea value={form.message} onChange={set("message")} placeholder={s.messagePh} rows={5} className="rounded-xl border-navy/15" data-testid="contact-input-message" />
                  </div>
                  <button type="submit" disabled={loading} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-electric px-7 py-4 text-sm font-bold text-white shadow-[0_8px_24px_rgba(30,127,240,0.4)] transition-all hover:-translate-y-0.5 disabled:opacity-60 sm:w-auto" data-testid="contact-submit">
                    {loading ? s.sending : <>{s.send} <PaperPlaneTilt size={18} weight="fill" /></>}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-5">
            <div className="space-y-4">
              {details.map((d, i) => (
                <div key={i} className="flex items-start gap-4 rounded-2xl border border-navy/[0.06] bg-white p-6 shadow-[0_8px_32px_rgba(30,127,240,0.06)]" data-testid={`contact-detail-${i}`}>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-electric/10 text-electric"><d.icon size={22} weight="duotone" /></div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{d.label}</p>
                    {d.href ? <a href={d.href} className="font-semibold text-navy hover:text-electric">{d.value}</a> : <p className="font-semibold leading-relaxed text-navy">{d.value}</p>}
                  </div>
                </div>
              ))}

              <div className="rounded-2xl border border-navy/[0.06] bg-white p-6 shadow-[0_8px_32px_rgba(30,127,240,0.06)]" data-testid="contact-business-details">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-electric/10 text-electric"><Buildings size={22} weight="duotone" /></span>
                  <p className="font-display text-base font-bold text-navy">{s.bizTitle}</p>
                </div>
                <dl className="mt-5 space-y-3 text-sm">
                  {[
                    [s.bizLegal, COMPANY.legal],
                    [s.bizCin, COMPANY.cin],
                    [s.bizGstin, COMPANY.gstin],
                    [s.bizOffice, COMPANY.address],
                    [s.lblPhone, COMPANY.phone],
                    [s.lblEmail, COMPANY.email],
                    [s.bizHours, COMPANY.supportHours],
                    [s.bizGrievance, `${COMPANY.grievanceName} · ${COMPANY.grievanceEmail} · ${s.bizGrievanceNote}`],
                  ].map(([k, v]) => (
                    <div key={k} className="grid gap-0.5">
                      <dt className="text-xs font-bold uppercase tracking-wider text-slate-500">{k}</dt>
                      <dd className="font-semibold leading-relaxed text-navy">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="flex items-center gap-4 rounded-2xl bg-navy p-6 text-white">
                <QrCode size={44} weight="duotone" className="text-electric-light" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{s.visit}</p>
                  <p className="font-display font-bold">{COMPANY.website}</p>
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-navy/[0.06] shadow-[0_8px_32px_rgba(30,127,240,0.06)]">
                <iframe
                  title="Priyajit Systems Location"
                  src="https://www.google.com/maps?q=Graphix+Tower+2+Sector+62+Noida&output=embed"
                  className="h-64 w-full border-0"
                  loading="lazy"
                  data-testid="contact-map"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
